/**
 * The persistent Ask Vera launcher — global chrome mounted once, inside `Header`
 * itself, rather than opted into per page.
 *
 * Before this, `SearchOverlay`'s ⌘K palette was the ONLY caller of `ask()` in the
 * entire app — the product's flagship capability was reachable exactly one way,
 * nested inside a keyboard shortcut, inside a search box. This is the second,
 * always-visible door onto the same panel.
 *
 * Mounted in `Header` (not passed as an opt-in prop a page might forget) for the
 * same reason `ReferrerBackLink` moved into a hook: working-conventions §42 records
 * that an opt-in `backLabel` prop was forgotten by 35 of 37 call sites. Global chrome
 * that depends on every page remembering to render it is chrome that mostly isn't
 * there.
 *
 * The shared `@repo/ui` `Button` at `variant="secondary"`, default size (40px — the page
 * header band, §29). It was a hand-rolled MUI `IconButton` until 2026-08-28; see "Why it
 * is the shared Button NOW" below for why the two blockers that forced the hand-roll no
 * longer apply.
 *
 * ── It is CONTAINED, and that is not decoration ─────────────────────────────────────
 * The first version rendered the mark alone: no border, no fill, 33px tall in a 40px
 * band. Two things were wrong with that, and they compounded.
 *
 * The V is the BRAND MARK. Unboxed, it reads as identity — which is exactly what it
 * means 60px away in the collapsed sidebar, where the same glyph at nearly the same
 * size is the logo. One glyph carrying both "this is Vera" and "press me" on one screen
 * is the same defect as a colour meaning two things at once: the reader cannot tell
 * which sense applies, so the mark competes with itself. A container resolves it
 * structurally rather than by hoping context disambiguates — an unboxed mark is a logo,
 * a mark inside a control is a button. And 33px sat 7px short of the 40px band (§29).
 *
 * ── Why it is the shared Button NOW ─────────────────────────────────────────────────
 * Two blockers are recorded below because both are actively misleading, and BOTH have
 * since been shown not to apply. The hand-roll they justified is gone (§28/§47: reuse
 * the shared component; do not imitate `secondary` with a bordered box).
 *
 * FIRST, a MUI `IconButton` carrying `fieldFill`/`fieldBorderColor` from `@repo/ui`:
 *
 *   error TS2345: Argument of type 'Theme' is not assignable to parameter of type 'Theme'
 *
 * pnpm resolves TWO copies of @mui/material (one under react 18.3.1 for the app, one
 * under 19.1.0 for `@repo/ui`), so those helpers are typed against a DIFFERENT nominal
 * `Theme` than a MUI component's `sx` gives. Identical structurally, incompatible
 * nominally; the only clue is a `react@18.3.1` vs `react@19.1.0` segment mid-path.
 *
 * That is real — 61 instances of it sit in the platform typecheck — but it is a problem
 * with CALLING a `@repo/ui` helper that takes a `Theme`. It says nothing about RENDERING
 * a `@repo/ui` component, which computes its own styles from its own theme context and
 * takes no `Theme` from us. And `ButtonProps` declares `sx?: any`, so even an `sx` theme
 * callback here is untyped and cannot reproduce it. (The underlying duplicate-instance
 * problem is separately being narrowed: `brandGradient.ts` introduces a structural
 * `BrandThemeLike` so new helpers stop adding to that pile.)
 *
 * SECOND, an earlier switch to the `@repo/ui` `Button` compiled and then blanked the app:
 *
 *   Cannot access 'MENU_ITEM_SX' before initialization
 *
 * a temporal-dead-zone error that reads exactly like an import cycle. It was NOT one.
 * Restarting the dev server cleared it: a stale Vite module graph after several rapid HMR
 * edits to this file. Recorded because the false diagnosis is the tempting one — TDZ on a
 * barrel export genuinely does mean a cycle most of the time, and it cost a wrong
 * conclusion here. If you see it, restart the server BEFORE concluding anything about the
 * module graph.
 *
 * So the container is now `variant="secondary"`: `primary[100]` border + `fieldFill`
 * surface + the 40px band, all owned by the shared component. The previous hand-roll had
 * the SAME border token by hand and NO fill — measured `rgba(0, 0, 0, 0)` in both modes,
 * i.e. fully transparent — which is what read as unfinished beside real controls.
 *
 * ⚠️ NOT "solid white in light, secondary in dark". That was the first phrasing of the
 * ask and it is the `ToggleButton` defect this codebase already carries: a hardcoded
 * per-mode colour (`alpha(common.black, 0.03)`) that reads as a visible groove on light
 * and measures `rgba(0,0,0,0)` on dark. One rule that RESOLVES per theme is correct; two
 * hardcoded values is the bug. `fieldFill` is that one rule.
 *
 * Still quiet by design. §26 gives the page exactly one primary action and this is not
 * it — `secondary` is the same weight every other non-primary control carries.
 *
 * ── The MARK is full-strength; the CONTAINER is what stays quiet ────────────────────
 * These are two separate decisions and conflating them is what made the first version
 * wrong. "Don't compete with the primary action" is a statement about WEIGHT — fill,
 * border, size — not about whether the glyph inside is legible.
 *
 * The mark originally inherited `currentColor` with no `color` set, so it fell to MUI
 * IconButton's default `rgba(0,0,0,0.54)`. At 54% a brand mark does not read as
 * restrained, it reads as DISABLED — the same alpha band the theme uses for genuinely
 * disabled text — which is the one thing an always-available control must never say.
 *
 * So: `common.black` / `common.white`, full strength, mode-inverted. Deliberately NOT
 * `text.primary`, which is `rgba(0,0,0,0.87)` in light (MUI's default — the light
 * palette never overrides it) and `#fafafa` in dark. Both are near-misses, and the ask
 * was full. This is also the exact pair `AskComposer`'s send button inverts on, so the
 * two Ask controls agree rather than each picking their own near-black.
 *
 * THIS SURVIVES THE MOVE TO `secondary`, and is the one property overridden below.
 * `secondary` would otherwise set `color: text.primary`, which is the near-miss this
 * paragraph already rejected. Overriding one token is not hand-rolling the variant —
 * the container (border, fill, height, radius, hover) is entirely the shared component's.
 *
 * It also carries the "is the V still findable?" requirement. A secondary launcher sits
 * in the same treatment as ordinary controls, so the MARK is what distinguishes it: a
 * full-strength brand glyph where its neighbours carry text or a muted lucide stroke.
 */
import { useTheme } from '@mui/material/styles'

// The shared tooltip, NOT `@mui/material`'s (§28/§47 — and this launcher is the case that
// motivated it). It sits hard against the right edge of the header, where MUI's centred
// placement overflows and Popper's `preventOverflow` shifts the tooltip back inboard,
// leaving it aligned to neither the anchor nor the edge. `@repo/ui`'s Tooltip measures
// both rects inside the positioning pipeline and aligns to the anchor's right edge
// instead; see the edge-aware-alignment note in its source.
import { Button, Tooltip } from '@repo/ui'

import { useAskStore } from 'ask'
import { AskVeraIcon } from 'components/display/AskVeraIcon/AskVeraIcon'
import { useShortcutBindings } from 'hooks'
import { formatBinding } from 'utils'

export const AskVeraLauncher = () => {
  const togglePanel = useAskStore(s => s.togglePanel)
  // Live binding, not a one-off read — a rebind in Settings must be reflected here
  // immediately, the same requirement every other shortcut-hint surface has.
  const { askToggle } = useShortcutBindings()
  // The app's OWN theme — read for `mode` only, never passed into a `@repo/ui` helper,
  // which is the distinction the docblock's FIRST blocker turns on.
  const { palette } = useTheme()

  return (
    <Tooltip title={`Ask Vera (${formatBinding(askToggle)})`}>
      <Button
        variant="secondary"
        // Literal, not `TOUR_ANCHORS.askVeraLauncher` — `check-tour-anchors.mjs`
        // matches a quoted string value textually (§56), the same convention every
        // other `data-tour` call site in the app follows (e.g. RolePage's
        // `data-tour="role-assign-users"`). Must stay in sync with
        // `TOUR_ANCHORS.askVeraLauncher` in `components/onboarding/tours.ts`.
        data-tour="ask-vera-launcher"
        aria-label="Ask Vera"
        onClick={togglePanel}
        sx={{
          // Square it. `Button` is sized for a label — `paddingX: 4` and
          // `minWidth: fit-content` — which on an icon-only trigger renders a wide
          // lozenge. Height (40), radius, border, fill and hover all stay the
          // variant's; only the horizontal box is overridden.
          width: 40,
          minWidth: 40,
          px: 0,
          // The one token deliberately overridden — see "The MARK is full-strength"
          // above. `secondary` sets `color: text.primary`; the mark wants full black /
          // full white. `AskVeraIcon` is `fill="currentColor"`, so this is what tints it.
          color: palette.mode === 'light' ? palette.common.black : palette.common.white,
        }}
      >
        <AskVeraIcon size={20} />
      </Button>
    </Tooltip>
  )
}
